import { Minus, Plus } from 'lucide-react'

import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@/components/ui/input-group'
import { Label } from '@/components/ui/label'

interface EnforcedTipInputProps {
  value?: number
  onChange: (value: number) => void
  disabled?: boolean
  organizationDefault?: number
  showOrganizationHint?: boolean
  label?: string
}

function clampPercentage(value: number): number {
  return Math.max(0, Math.min(100, value))
}

function parsePercentage(raw: string): number {
  const parsed = Number.parseFloat(raw)
  return Number.isNaN(parsed) ? 0 : parsed
}

/**
 * Percentage input with increment/decrement buttons. Clamps to 0–100.
 */
export function EnforcedTipInput({
  value = 0,
  onChange,
  disabled = false,
  organizationDefault,
  showOrganizationHint = false,
  label = 'Enforced Tip (%)',
}: EnforcedTipInputProps) {
  return (
    <div className="grid min-w-0 gap-3">
      <div>
        <Label htmlFor="enforced-tip-percentage">{label}</Label>
        {showOrganizationHint && organizationDefault && organizationDefault > 0 && (
          <p className="text-xs text-muted-foreground mt-1">
            Default tip for this event type: {organizationDefault}%
          </p>
        )}
      </div>
      <InputGroup>
        <InputGroupAddon align="inline-start" className="pl-1">
          <InputGroupButton
            size="icon-sm"
            aria-label="Decrease tip percentage"
            onClick={() => onChange(clampPercentage(value - 1))}
            disabled={disabled || value <= 0}
          >
            <Minus />
          </InputGroupButton>
        </InputGroupAddon>
        <InputGroupInput
          id="enforced-tip-percentage"
          type="number"
          min="0"
          max="100"
          step="1"
          value={value || ''}
          onChange={(event) => onChange(parsePercentage(event.target.value))}
          onBlur={(event) => onChange(clampPercentage(parsePercentage(event.target.value)))}
          disabled={disabled}
          className="text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        />
        <InputGroupAddon align="inline-end" className="pr-1">
          <InputGroupButton
            size="icon-sm"
            aria-label="Increase tip percentage"
            onClick={() => onChange(clampPercentage(value + 1))}
            disabled={disabled || value >= 100}
          >
            <Plus />
          </InputGroupButton>
        </InputGroupAddon>
      </InputGroup>
    </div>
  )
}
